// filepath: apps/web/src/operator/TenantDrillDown.tsx
//
// FR-UI-OPS-2 AC-3 / FR-UI-OPS-3 — per-tenant drill-down dialog.
//
// Opened from a `<SummaryTable>` row click (wired in
// `OperatorSummaryRoute`). Renders the per-tenant fields already
// present in the `useOperatorSummary` payload — `instanceCount`,
// `errorCount`, `activeSandboxCount` — plus a deep link to that
// tenant's own `/admin` subdomain (URL constructed from the tenant
// `slug`). Opening and closing the dialog is a pure in-SPA state
// transition (no full page reload). Following the deep link is an
// explicit user action that navigates away from the operator console
// to the tenant subdomain.
//
// SECURITY (INV-9): the tenant `slug` is rendered as a text node (in
// `<code>`) and as an `href` attribute value. It is NEVER rendered
// as raw HTML — no `dangerouslySetInnerHTML`. The `href` is built
// from the slug + platform apex via string concatenation; React
// escapes attribute values, so a slug containing markup cannot inject.
//
// SCOPE (AC-3): NO quota field is rendered. The v1 `GET /admin/summary`
// payload (`SummaryTenantRow`) carries no quota — surfacing quota would
// require a backend change (descoped to a future observability FR).
//
// FR-UI-OPS-3 redesign: dialog chrome is now composed on top of the
// design-system `Dialog` primitive (overlay + panel + token-driven
// typography). The semantic contract (`role="dialog"`,
// `aria-modal="true"`, deep-link `href`, `data-testid` hooks, focus
// trap, no reload on close) is PRESERVED verbatim.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { focusTrap } from "@/runtime/focusTrap";
import { Button, Dialog } from "@/design-system";
import type { SummaryTenantRow } from "./useOperatorSummary";

/**
 * Fallback apex when the build-time env var is absent. Mirrors
 * `isOperatorSurface.ts` so the two resolve to the same default.
 */
const DEFAULT_PLATFORM_APEX = "platform.example";

/**
 * Build the deep link to a tenant's own `/admin` surface. The tenant
 * SPA is served on `<slug>.<platformApex>`; the operator surface is
 * served on the bare apex. The URL is `https://<slug>.<apex>/admin`.
 *
 * Pure: no side effects. Exported for TestDesigner to pin the
 * construction without rendering the dialog.
 */
export function buildTenantAdminUrl(slug: string): string {
  const raw = (import.meta.env.VITE_PLATFORM_APEX ?? "") as string;
  const apex = raw.trim().length > 0 ? raw.trim() : DEFAULT_PLATFORM_APEX;
  return `https://${slug}.${apex}/admin`;
}

export interface TenantDrillDownProps {
  /** The tenant row whose details are shown. */
  row: SummaryTenantRow;
  /** Close the dialog (clears the parent's selected-tenant state). */
  onClose: () => void;
}

/**
 * TenantDrillDown — modal dialog. The visual chrome is the
 * design-system `Dialog` primitive. Focus is trapped on mount
 * (FR-UI-CORE-9) and restored on unmount. The Close button is the
 * initial focus (safer default than the external deep link).
 *
 * The dialog is controlled by `open={true}` (it only mounts when the
 * parent passes a non-null `row`). The Close button wires to `onClose`
 * which clears the parent's selected-tenant state (pure in-SPA
 * transition).
 */
export function TenantDrillDown({
  row,
  onClose,
}: TenantDrillDownProps): React.ReactElement {
  const { t } = useTranslation();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return undefined;
    const closeButton = el.querySelector<HTMLButtonElement>(
      'button[data-testid="tenant-drilldown-close"]',
    );
    return focusTrap(el, { initialFocus: closeButton ?? null });
  }, []);

  const adminUrl = buildTenantAdminUrl(row.slug);

  return (
    <Dialog
      open
      onClose={onClose}
      data-testid="tenant-drilldown"
      aria-labelledby="tenant-drilldown-title"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tenant-drilldown-title"
        data-testid="tenant-drilldown-panel"
      >
        <div
          className="tenant-drilldown-header"
          style={{ marginBottom: "var(--space-4)" }}
        >
          <h2
            id="tenant-drilldown-title"
            style={{
              fontSize: "var(--text-lg)",
              fontWeight: "var(--font-semibold)",
              color: "var(--text-primary)",
              margin: 0,
            }}
          >
            {t("operator.drillDown.title", "Tenant details")}{" "}
            <code
              style={{
                fontFamily: "var(--font-mono, monospace)",
                fontSize: "var(--text-base)",
                color: "var(--text-primary)",
              }}
            >
              {row.slug}
            </code>
          </h2>
        </div>

        <dl
          className="tenant-drilldown-stats"
          data-testid="tenant-drilldown-stats"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "var(--space-4)",
            margin: 0,
          }}
        >
          <div className="tenant-drilldown-stat">
            <dt
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--text-secondary)",
                marginBottom: "var(--space-2)",
              }}
            >
              {t("operator.drillDown.instanceCount", "Active instances")}
            </dt>
            <dd
              data-testid="tenant-drilldown-instance-count"
              style={{
                fontSize: "var(--text-2xl)",
                fontWeight: "var(--font-bold)",
                color: "var(--text-primary)",
                margin: 0,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.instanceCount}
            </dd>
          </div>
          <div className="tenant-drilldown-stat">
            <dt
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--text-secondary)",
                marginBottom: "var(--space-2)",
              }}
            >
              {t("operator.drillDown.errorCount", "Errors")}
            </dt>
            <dd
              data-testid="tenant-drilldown-error-count"
              style={{
                fontSize: "var(--text-2xl)",
                fontWeight: "var(--font-bold)",
                color:
                  row.errorCount > 0
                    ? "var(--color-danger-hover)"
                    : "var(--text-primary)",
                margin: 0,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.errorCount}
            </dd>
          </div>
          <div className="tenant-drilldown-stat">
            <dt
              style={{
                fontSize: "var(--text-xs)",
                color: "var(--text-secondary)",
                marginBottom: "var(--space-2)",
              }}
            >
              {t("operator.drillDown.activeSandboxCount", "Active sandboxes")}
            </dt>
            <dd
              data-testid="tenant-drilldown-active-sandbox-count"
              style={{
                fontSize: "var(--text-2xl)",
                fontWeight: "var(--font-bold)",
                color: "var(--text-primary)",
                margin: 0,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {row.activeSandboxCount}
            </dd>
          </div>
        </dl>

        <p
          className="tenant-drilldown-admin"
          style={{ marginTop: "var(--space-6)" }}
        >
          <a
            href={adminUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="tenant-drilldown-admin-link"
            style={{
              color: "var(--color-primary)",
              textDecoration: "underline",
              fontFamily: "var(--font-sans)",
            }}
          >
            {t("operator.drillDown.openAdmin", "Open tenant /admin")}
          </a>
        </p>

        <footer
          className="tenant-drilldown-footer"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "var(--space-2)",
            marginTop: "var(--space-6)",
          }}
        >
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            data-testid="tenant-drilldown-close"
            className="tenant-drilldown-close"
          >
            {t("operator.drillDown.close", "Close")}
          </Button>
        </footer>
      </div>
    </Dialog>
  );
}
